import { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import logo from './logo.png';
import FreeDropdown from './FreeDropdown';
import VideoPopup from './VideoPopup';
import DiveSessionHistoryOverlay from './DiveSessionHistoryOverlay';
import ChatWelcomeOverlay from './ChatWelcomePage';
import { normalizeDiscogsUrl, extractReleaseId, isMasterRelease } from '../utils/discogsUrlNormalizer';

// Smart API URL switching
const API_BASE_URL =
    window.location.hostname === "staging187611.digassistant.xyz"
        ? "https://api.digassistant.xyz"
        : window.location.hostname === "digassistant.xyz" || window.location.hostname === "www.digassistant.xyz"
            ? "https://api.digassistant.xyz"
            : "http://localhost:8080";

const extractYouTubeId = (url) => {
    if (url?.includes('youtube.com/watch?v=')) return url.split('v=')[1]?.split('&')[0];
    if (url?.includes('youtu.be/')) return url.split('youtu.be/')[1]?.split('?')[0];
    return null;
};

const filterAvailableVideos = async (urls) => {
    const results = await Promise.all(
        urls.map(async (url) => {
            const id = extractYouTubeId(url);
            if (!id) return null;
            try {
                const res = await fetch(
                    `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${id}&format=json`
                );
                if (!res.ok) return null;
                const data = await res.json();
                return { url, title: data.title || '' };
            } catch {
                return null;
            }
        })
    );
    return results.filter(Boolean);
};

const AdvancedServicePage = () => {
    const chatMode = true;
    const [url, setUrl] = useState('');
    const [error, setError] = useState('');
    const [urlError, setUrlError] = useState('');
    const [showChatWelcome, setShowChatWelcome] = useState(() => !localStorage.getItem('chatWelcomeShown'));
    const [showQueryHint, setShowQueryHint] = useState(false);
    const [releases, setReleases] = useState([]);
    const [recArtists, setRecArtists] = useState([]);
    const [recTitles, setRecTitles] = useState([]);
    const [videosByReleaseId, setVideosByReleaseId] = useState({});
    const [selectedVideoPopup, setSelectedVideoPopup] = useState(null);

    const [deepCut, setDeepCut] = useState(null); // ⬅️ NEW
    const [startIndex, setStartIndex] = useState(0); // Starting index for sliding window (always show 5)

    const [notFound, setNotFound] = useState(false);
    const [visible, setVisible] = useState(false);
    const [notFoundMessage, setNotFoundMessage] = useState('');
    const [isDive, setIsDive] = useState(false);
    const [diveStack, setDiveStack] = useState([]);
    const navigate = useNavigate();
    const [showDiveHistory, setShowDiveHistory] = useState(false);
    const [showDeepCutPanel, setShowDeepCutPanel] = useState(false);

    const [shouldShowLowResultsMessage, setShouldShowLowResultsMessage] = useState(false);
    const [consecutiveLowResultsCount, setConsecutiveLowResultsCount] = useState(0);
    const inputPartialRef = useRef(null);
    const textareaRef = useRef(null);
    const placeholderStoppedRef = useRef(false);
    const fetchingRef = useRef(false);
    const rateLimitedRef = useRef(false);
    const [clarifyMessage, setClarifyMessage] = useState('');
    const [clarifyDisplay, setClarifyDisplay] = useState('');
    const [searchSummary, setSearchSummary] = useState('');
    const [loading, setLoading] = useState(false);
    const [lastSearchText, setLastSearchText] = useState('');
    const [placeholderText, setPlaceholderText] = useState('');
    const [placeholderFading, setPlaceholderFading] = useState(false);
    const [loadingDots, setLoadingDots] = useState('.');
    const [searchLabel, setSearchLabel] = useState('');

    useEffect(() => {
        if (url === '' && textareaRef.current) {
            textareaRef.current.style.height = '';
        }
    }, [url]);

    useEffect(() => {
        const text = 'Find records similar to:';
        let i = 0;
        const id = setInterval(() => {
            if (i < text.length) {
                setSearchLabel(text.slice(0, ++i));
            } else {
                clearInterval(id);
            }
        }, 30);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const examples = [
            "Early 80s Peter Tosh",
            "Early 2000s West Coast US House like Rithma",
            "70s funk like James Brown",
            "Dub like Aba-shant i 90s style UK",
            "Describe what you're after",
            "Underground Resistance - The Final Frontier",
        ];
        let exIdx = 0;
        let charIdx = 0;
        let timeoutId;

        const typeNext = () => {
            if (placeholderStoppedRef.current) return;
            const current = examples[exIdx];
            if (charIdx < current.length) {
                charIdx++;
                setPlaceholderText(current.slice(0, charIdx));
                timeoutId = setTimeout(typeNext, 25);
            } else {
                timeoutId = setTimeout(() => {
                    if (placeholderStoppedRef.current) return;
                    setPlaceholderFading(true);
                    timeoutId = setTimeout(() => {
                        setPlaceholderFading(false);
                        charIdx = 0;
                        setPlaceholderText('');
                        exIdx = (exIdx + 1) % examples.length;
                        timeoutId = setTimeout(typeNext, 100);
                    }, 400);
                }, 1500);
            }
        };

        timeoutId = setTimeout(typeNext, 550);
        return () => clearTimeout(timeoutId);
    }, [placeholderStoppedRef]);

    useEffect(() => {
        if (searchSummary) {
            placeholderStoppedRef.current = true;
            setPlaceholderText('');
            setPlaceholderFading(false);
        }
    }, [searchSummary, placeholderStoppedRef]);

    useEffect(() => {
        if (!loading) { setLoadingDots('.'); return; }
        const id = setInterval(() => {
            setLoadingDots(prev => prev.length >= 3 ? '.' : prev + '.');
        }, 400);
        return () => clearInterval(id);
    }, [loading]);

    useEffect(() => {
        if (!clarifyMessage) { setClarifyDisplay(''); return; }
        let i = 0;
        const id = setInterval(() => {
            i++;
            setClarifyDisplay(clarifyMessage.slice(0, i));
            if (i >= clarifyMessage.length) clearInterval(id);
        }, 22);
        return () => clearInterval(id);
    }, [clarifyMessage]);

    // Get consecutive <= 3 results count from sessionStorage
    const getConsecutiveLowResultsCount = () => {
        const count = sessionStorage.getItem('consecutiveLowResultsCount');
        return count ? parseInt(count, 10) : 0;
    };

    const incrementConsecutiveLowResultsCount = () => {
        const currentCount = getConsecutiveLowResultsCount();
        const newCount = currentCount + 1;
        sessionStorage.setItem('consecutiveLowResultsCount', newCount.toString());
        setConsecutiveLowResultsCount(newCount);
    };

    const resetConsecutiveLowResultsCount = () => {
        sessionStorage.removeItem('consecutiveLowResultsCount');
        setConsecutiveLowResultsCount(0);
    };

    useEffect(() => {
        // Initialize consecutive count from sessionStorage on mount
        const count = sessionStorage.getItem('consecutiveLowResultsCount');
        setConsecutiveLowResultsCount(count ? parseInt(count, 10) : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const fetchReleases = async (releaseId) => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        try {
            // reset
            setReleases([]);
            setRecArtists([]);
            setRecTitles([]);
            setDeepCut(null);
            setNotFound(false);
            setNotFoundMessage('');
            setError('');
            setUrlError('');
            setIsDive(true); // URL path has no loading phase — skip the 1.5s delayed fade-in

            const response = await axios.get(`${API_BASE_URL}/recommend/seed/${releaseId}`, { withCredentials: true });

            // Server shape assumed:
            // {
            //   label: "Some label",
            //   recommendations: {
            //     recommendations: [...ids],
            //     recommendations_artist: [...],
            //     recommendations_title: [...],
            //     recommendations_label: [...]
            //   },
            //   deep_cut: { release_id, artist, title, label } | null
            // }
            // (unused here but kept if you want later)
            const { recommendations: recData, deep_cut, videos } = response.data;

            // Defensive: ensure arrays are aligned to min length
            const len = Math.min(
                recData.recommendations?.length ?? 0,
                recData.recommendations_artist?.length ?? 0,
                recData.recommendations_title?.length ?? 0,
                recData.recommendations_label?.length ?? 0
            );

            const recommendations = Array.from({ length: len }, (_, index) => ({
                releaseId: recData.recommendations[index],
                artist: recData.recommendations_artist[index],
                title: recData.recommendations_title[index],
                label: recData.recommendations_label[index],
            }));

            const releaseIds = recommendations.map(({ releaseId }) => releaseId);
            setReleases(releaseIds);
            setRecArtists(recommendations.map(({ artist }) => artist));
            setRecTitles(recommendations.map(({ title }) => title));
            setStartIndex(0); // Reset pagination when new recommendations are fetched
            setUrl('');

            // Show message logic: <= 3 results always show "thin" message
            // If 3 consecutive <= 3 results, show "going in circles" message
            if (releaseIds.length <= 3) {
                // Increment consecutive count and always show message
                incrementConsecutiveLowResultsCount();
                setShouldShowLowResultsMessage(true);
            } else {
                // Results > 3: reset consecutive count, don't show message
                resetConsecutiveLowResultsCount();
                setShouldShowLowResultsMessage(false);
            }

            // Store videos for the searched release if present
            if (videos && videos.videos && videos.videos.length > 0) {
                setVideosByReleaseId(prev => ({
                    ...prev,
                    [releaseId]: videos.videos
                }));
            }

            // Fetch videos for each recommendation (in background, don't block)
            const videoPromises = recommendations.map(async (rec) => {
                if (rateLimitedRef.current) return;
                try {
                    const videoResponse = await fetch(`${API_BASE_URL}/videos/${rec.releaseId}`, {
                        credentials: 'include'
                    });
                    if (videoResponse.ok) {
                        const data = await videoResponse.json();
                        if (data?.videos?.video && data.videos.video.length > 0) {
                            const available = await filterAvailableVideos(data.videos.video);
                            if (available.length > 0) {
                                setVideosByReleaseId(prev => ({
                                    ...prev,
                                    [rec.releaseId]: available
                                }));
                            }
                        }
                    }
                } catch (error) {
                    // Silently fail
                }
            });

            // Set deep cut if present and well-formed
            if (deep_cut && deep_cut.release_id && deep_cut.title) {
                setDeepCut({
                    releaseId: deep_cut.release_id,
                    artist: deep_cut.artist,
                    title: deep_cut.title,
                    label: deep_cut.label,
                });

                // Fetch videos for deep cut in background
                videoPromises.push(
                    (async () => {
                        if (rateLimitedRef.current) return;
                        try {
                            const videoResponse = await fetch(`${API_BASE_URL}/videos/${deep_cut.release_id}`, {
                                credentials: 'include'
                            });
                            if (videoResponse.ok) {
                                const data = await videoResponse.json();
                                if (data?.videos?.video && data.videos.video.length > 0) {
                                    const available = await filterAvailableVideos(data.videos.video);
                                    if (available.length > 0) {
                                        setVideosByReleaseId(prev => ({
                                            ...prev,
                                            [deep_cut.release_id]: available
                                        }));
                                    }
                                }
                            }
                        } catch (error) {
                            // Silently fail
                        }
                    })()
                );
            } else {
                setDeepCut(null);
            }

            // Execute all video fetches in parallel
            Promise.allSettled(videoPromises);
        } catch (error) {
            // console.error('Fetch error:', error);

            if (error.response) {
                const status = error.response.status;
                const serverMessage =
                    error.response.data?.error ||
                    error.response.data?.message ||
                    null;

                if (status === 404) {
                    setNotFoundMessage(
                        "Dig Assistant focuses on electronic, reggae, soul &amp; funk vinyl<br />and will expand to newer releases and genres over time<br />We may not have a suitable recommendation for this yet :)"
                    );
                } else if (status === 400) {
                    setNotFoundMessage(serverMessage || "Invalid release ID. Please check the URL format.");
                } else if (status === 429) {
                    rateLimitedRef.current = true;
                    setNotFoundMessage("You've reached the edge of the wormhole! Explore more by becoming a member");
                } else if (status === 502) {
                    setNotFoundMessage("Internal server error. Please try again later.");
                } else if (serverMessage) {
                    setNotFoundMessage(serverMessage);
                } else {
                    setNotFoundMessage("An unknown error occurred.");
                }
            } else {
                setNotFoundMessage("A network error occurred. Please check your connection.");
            }

            setNotFound(true);
            setVisible(true);
        } finally {
            fetchingRef.current = false;
        }
    };

    const goBack = () => {
        setDiveStack(prev => {
            const stack = [...prev];
            const previous = stack.pop();
            if (!previous) return prev;
            setReleases(previous.releases);
            setRecArtists(previous.recArtists);
            setRecTitles(previous.recTitles);
            setDeepCut(previous.deepCut);
            setStartIndex(0);
            setVisible(true);
            if (stack.length === 0) setIsDive(false);
            return stack;
        });
    };

    const fetchReleasesDive = async (releaseId) => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        setIsDive(true);
        setDiveStack(prev => [...prev, { releases, recArtists, recTitles, deepCut }]);
        try {
            // reset
            setReleases([]);
            setRecArtists([]);
            setRecTitles([]);
            setDeepCut(null);
            setNotFound(false);
            setNotFoundMessage('');
            setError('');
            setUrlError('');

            const response = await axios.get(`${API_BASE_URL}/recommend/dive/${releaseId}`, { withCredentials: true });

            const { recommendations: recData, deep_cut, videos } = response.data;

            // Defensive: ensure arrays are aligned to min length
            const len = Math.min(
                recData.recommendations?.length ?? 0,
                recData.recommendations_artist?.length ?? 0,
                recData.recommendations_title?.length ?? 0,
                recData.recommendations_label?.length ?? 0
            );

            const recommendations = Array.from({ length: len }, (_, index) => ({
                releaseId: recData.recommendations[index],
                artist: recData.recommendations_artist[index],
                title: recData.recommendations_title[index],
                label: recData.recommendations_label[index],
            }));

            const releaseIds = recommendations.map(({ releaseId }) => releaseId);
            setReleases(releaseIds);
            setRecArtists(recommendations.map(({ artist }) => artist));
            setRecTitles(recommendations.map(({ title }) => title));
            setStartIndex(0); // Reset pagination when new recommendations are fetched
            setUrl('');

            // Show message logic: <= 3 results always show "thin" message
            // If 3 consecutive <= 3 results, show "going in circles" message
            if (releaseIds.length <= 3) {
                // Increment consecutive count and always show message
                incrementConsecutiveLowResultsCount();
                setShouldShowLowResultsMessage(true);
            } else {
                // Results > 3: reset consecutive count, don't show message
                resetConsecutiveLowResultsCount();
                setShouldShowLowResultsMessage(false);
            }

            // Store videos for the searched release if present
            if (videos && videos.videos && videos.videos.length > 0) {
                setVideosByReleaseId(prev => ({
                    ...prev,
                    [releaseId]: videos.videos
                }));
            }

            // Fetch videos for each recommendation (in background, don't block)
            const videoPromises = recommendations.map(async (rec) => {
                if (rateLimitedRef.current) return;
                try {
                    const videoResponse = await fetch(`${API_BASE_URL}/videos/${rec.releaseId}`, {
                        credentials: 'include'
                    });
                    if (videoResponse.ok) {
                        const data = await videoResponse.json();
                        if (data?.videos?.video && data.videos.video.length > 0) {
                            const available = await filterAvailableVideos(data.videos.video);
                            if (available.length > 0) {
                                setVideosByReleaseId(prev => ({
                                    ...prev,
                                    [rec.releaseId]: available
                                }));
                            }
                        }
                    }
                } catch (error) {
                    // Silently fail
                }
            });

            // Set deep cut if present and well-formed
            if (deep_cut && deep_cut.release_id && deep_cut.title) {
                setDeepCut({
                    releaseId: deep_cut.release_id,
                    artist: deep_cut.artist,
                    title: deep_cut.title,
                    label: deep_cut.label,
                });

                // Fetch videos for deep cut in background
                videoPromises.push(
                    (async () => {
                        if (rateLimitedRef.current) return;
                        try {
                            const videoResponse = await fetch(`${API_BASE_URL}/videos/${deep_cut.release_id}`, {
                                credentials: 'include'
                            });
                            if (videoResponse.ok) {
                                const data = await videoResponse.json();
                                if (data?.videos?.video && data.videos.video.length > 0) {
                                    const available = await filterAvailableVideos(data.videos.video);
                                    if (available.length > 0) {
                                        setVideosByReleaseId(prev => ({
                                            ...prev,
                                            [deep_cut.release_id]: available
                                        }));
                                    }
                                }
                            }
                        } catch (error) {
                            // Silently fail
                        }
                    })()
                );
            } else {
                setDeepCut(null);
            }

            // Execute all video fetches in parallel
            Promise.allSettled(videoPromises);
        } catch (error) {
            if (error.response) {
                const status = error.response.status;
                const serverMessage =
                    error.response.data?.error ||
                    error.response.data?.message ||
                    null;

                if (status === 404) {
                    setNotFoundMessage(
                        "Dig Assistant focuses on electronic, reggae, soul &amp; funk vinyl<br />and will expand to newer releases and genres over time<br />We may not have a suitable recommendation for this yet :)"
                    );
                } else if (status === 400) {
                    setNotFoundMessage(serverMessage || "Invalid release ID. Please check the URL format.");
                } else if (status === 429) {
                    rateLimitedRef.current = true;
                    setNotFoundMessage("You've reached the edge of the wormhole! Explore more by becoming a member");
                } else if (status === 502) {
                    setNotFoundMessage("Internal server error. Please try again later.");
                } else if (serverMessage) {
                    setNotFoundMessage(serverMessage);
                } else {
                    setNotFoundMessage("An unknown error occurred.");
                }
            } else {
                setNotFoundMessage("A network error occurred. Please check your connection.");
            }

            setNotFound(true);
            setVisible(true);
        } finally {
            fetchingRef.current = false;
        }
    };

    useEffect(() => {
        if (notFound || releases.length > 0 || !!lastSearchText) {
            setVisible(true);
        } else {
            setVisible(false);
        }
    }, [notFound, releases, lastSearchText]);

    useEffect(() => {
        if (releases.length === 0 || releases.length >= 4 || localStorage.getItem('queryHintDismissed')) return;
        setShowQueryHint(true);
    }, [releases]);

    const isValidUrl = (url) => {
        // Require discogs.com to be followed by end of string, path separator, query, or fragment
        // This prevents fake domains like discogs.com.fake.com from passing
        const regex = /^(https?:\/\/)?(www\.)?discogs\.com([/?#]|$)/i;
        return regex.test(url);
    };

    const looksLikeUrl = (input) => {
        return /^(https?:\/\/|www\.)/i.test(input);
    };

    const buildSearchSummary = (query) => {
        const parts = [];
        const styles = [...(query.style || []), ...(query.genre || [])];
        if (styles.length) parts.push(styles.slice(0, 2).join(' / '));
        if (query.year_range) parts.push(`${query.year_range[0]}–${query.year_range[1]}`);

        if (query.country) parts.push(`from ${query.country}`);
        if (query.artist) {
            const anchor = query.track ? `similar to ${query.artist} – ${query.track}` : `Like artist: ${query.artist}`;
            parts.push(anchor);
        } else if (query.label) {
            parts.push(`on ${query.label}`);
        } else {
            parts.push('found no matching artist or label in user input');
        }
        return parts.join(' | ');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setUrlError('');
        setError('');
        setSearchSummary('');
        setNotFound(false);
        setNotFoundMessage('');
        const accumulatedSearch = inputPartialRef.current
            ? `${lastSearchText} · ${url.trim()}`
            : url.trim();
        setLastSearchText('');
        resetConsecutiveLowResultsCount(); // Reset consecutive tracking on new search

        // Clear previous results immediately to prevent flash of old results
        setReleases([]);
        setRecArtists([]);
        setRecTitles([]);
        setDeepCut(null);
        setVisible(false);
        setIsDive(false);
        setDiveStack([]);

        const trimmedUrl = url.trim();
        setUrl(trimmedUrl); // Update state with trimmed value
        if (chatMode && isValidUrl(trimmedUrl)) {
            setError('discogs-url-in-voyage');
            return;
        }
        if (looksLikeUrl(trimmedUrl) && !chatMode) {
            if (!isValidUrl(trimmedUrl)) {
                setError('invalid-url');
                return;
            }
            // Normalize URL to strip locale prefixes (e.g., /en/, /fr/, etc.)
            const normalizedUrl = normalizeDiscogsUrl(trimmedUrl);

            // Check if it's a master release
            if (isMasterRelease(normalizedUrl)) {
                // Handle master release: get random release_id from master
                const masterId = extractReleaseId(normalizedUrl);
                if (masterId) {
                    setError('');
                    try {
                        const masterResponse = await axios.get(`${API_BASE_URL}/master/${masterId}`, { withCredentials: true });
                        if (masterResponse.data && masterResponse.data.release_id) {
                            fetchReleases(masterResponse.data.release_id);
                        } else {
                            setError('invalid-url');
                        }
                    } catch (error) {
                        console.error('Error fetching master release:', error);
                        if (error.response) {
                            const status = error.response.status;
                            const serverMessage =
                                error.response.data?.error ||
                                error.response.data?.message ||
                                null;

                            if (status === 404) {
                                setNotFoundMessage(
                                    "Dig Assistant focuses on electronic, reggae, soul &amp; funk vinyl<br />and will expand to newer releases and genres over time<br />We may not have a suitable recommendation for this yet :)"
                                );
                                setNotFound(true);
                            } else if (status === 429) {
                                rateLimitedRef.current = true;
                                setNotFoundMessage("You've reached the edge of the wormhole! Explore more by becoming a member");
                                setNotFound(true);
                            } else if (serverMessage) {
                                setNotFoundMessage(serverMessage);
                                setNotFound(true);
                            } else {
                                setError('invalid-url');
                            }
                        } else {
                            setError('invalid-url');
                        }
                    }
                } else {
                    setError('invalid-url');
                }
            } else {
                // Handle regular release
                const releaseId = extractReleaseId(normalizedUrl);
                if (releaseId) {
                    setError('');
                    fetchReleases(releaseId);
                } else {
                    setError('invalid-url');
                }
            }
        } else {
            // LLM flow — input doesn't look like a URL
            setUrl('');
            setLastSearchText(accumulatedSearch);
            setVisible(true);
            setLoading(true);
            try {
                const body = inputPartialRef.current
                    ? { text: trimmedUrl, partial: inputPartialRef.current }
                    : { text: trimmedUrl };
                const response = await axios.post(`${API_BASE_URL}/input/parse`, body, { withCredentials: true });
                const result = response.data;

                if (result.type === 'clarify') {
                    setClarifyMessage(result.message);
                    inputPartialRef.current = result.partial;
                    setUrl('');
                } else {
                    setClarifyMessage('');
                    inputPartialRef.current = null;
                    if (result.query) setSearchSummary(buildSearchSummary(result.query));
                    if (result.atlas_candidates && result.atlas_candidates.length > 0) {
                        setReleases(result.atlas_candidates.map(c => String(c.release_id)));
                        setRecArtists(result.atlas_candidates.map(c => c.artist));
                        setRecTitles(result.atlas_candidates.map(c => c.title));
                        setDeepCut(null);
                        setStartIndex(0);
                        setShouldShowLowResultsMessage(false);
                        result.atlas_candidates.forEach(async (candidate) => {
                            if (rateLimitedRef.current) return;
                            try {
                                const vr = await fetch(`${API_BASE_URL}/videos/${candidate.release_id}`, { credentials: 'include' });
                                if (vr.ok) {
                                    const vdata = await vr.json();
                                    if (vdata?.videos?.video?.length > 0) {
                                        const available = await filterAvailableVideos(vdata.videos.video);
                                        if (available.length > 0) {
                                            setVideosByReleaseId(prev => ({ ...prev, [String(candidate.release_id)]: available }));
                                        }
                                    }
                                }
                            } catch {}
                        });
                    } else if (result.seed_release_id) {
                        await fetchReleases(result.seed_release_id);
                    } else {
                        setError('Wormhole found no good candidates, try rephrasing or a direct Discogs URL');
                    }
                }
            } catch (err) {
                if (err.response?.status === 429) {
                    rateLimitedRef.current = true;
                    setNotFoundMessage("You've reached the edge of the wormhole! Explore more by becoming a member");
                    setNotFound(true);
                    setVisible(true);
                } else {
                    setError('Wormhole malfunction, please try again.');
                }
            } finally {
                setLoading(false);
            }
        }
    };

    const openYoutubePlaylist = () => {
        const currentReleaseIds = [
            ...releases,
            ...(deepCut ? [deepCut.releaseId] : [])
        ].map(String);
        const videoIds = currentReleaseIds
            .flatMap(id => {
                const videos = videosByReleaseId[id] || [];
                if (videos.length === 0) return [];
                const first = videos[0];
                const firstId = extractYouTubeId(typeof first === 'string' ? first : first.url);
                const ids = firstId ? [firstId] : [];
                const second = videos[1];
                if (second) {
                    const t1 = (typeof first === 'string' ? '' : first.title).toLowerCase().trim();
                    const t2 = (typeof second === 'string' ? '' : second.title).toLowerCase().trim();
                    if (t1 !== t2) {
                        const secondId = extractYouTubeId(typeof second === 'string' ? second : second.url);
                        if (secondId) ids.push(secondId);
                    }
                }
                return ids;
            })
            .filter(Boolean);
        if (videoIds.length > 0) {
            window.open(`https://www.youtube.com/watch_videos?video_ids=${videoIds.join(',')}`, '_blank');
        }
    };

    return (
        <div>
            <FreeDropdown GuideComponent={ChatWelcomeOverlay} />
            <div className="App">
                <a href="https://www.timehri.world/music" target="_blank" rel="noopener noreferrer">
                    <img src={logo} alt="Logo" className="logo" />
                </a>


                {error && error !== 'invalid-url' && error !== 'discogs-url-in-voyage' && !error.startsWith('Wormhole') && <div className="error-message">{error}</div>}

                {error && error.startsWith('Wormhole') && (
                    <div style={{
                        textAlign: 'center',
                        position: 'absolute',
                        top: window.innerWidth <= 600 ? '22%' : '28%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '100%',
                        maxWidth: '600px',
                        padding: window.innerWidth <= 600 ? '0 20px' : '0'
                    }}>
                        <div style={{
                            color: '#009e4a',
                            fontFamily: 'CustomFont',
                            fontSize: window.innerWidth <= 600 ? '0.65rem' : '0.75rem',
                            lineHeight: '1.6',
                            marginBottom: '20px',
                            opacity: 0.8
                        }}>
                            {error}
                        </div>
                        <img
                            src="https://images.squarespace-cdn.com/content/v1/5f30083236006c73677947bf/1597164454026-1NTL28QID02MZ7Y71VZD/WORMHOLE_GREEN.png?format=1500w"
                            alt="wormhole"
                            className="floating-wormhole"
                            style={{ position: 'relative', width: '150px', height: 'auto', opacity: 0.2 }}
                        />
                    </div>
                )}

                {error && error === 'invalid-url' && (
                    <div style={{
                        textAlign: 'center',
                        position: 'absolute',
                        top: window.innerWidth <= 600 ? '22%' : '28%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '100%',
                        maxWidth: '600px',
                        marginBottom: '20px',
                        padding: window.innerWidth <= 600 ? '0 20px' : '0'
                    }}>
                        <div style={{
                            color: '#009e4a',
                            fontFamily: 'CustomFont',
                            fontSize: window.innerWidth <= 600 ? '0.65rem' : '0.75rem',
                            lineHeight: '1.6',
                            marginBottom: '20px',
                            opacity: 0.8
                        }}>
                            <span style={{ whiteSpace: window.innerWidth <= 600 ? 'normal' : 'nowrap' }}>Oops! Dig Assistant works with Discogs vinyl pages.</span><br />
                            Try pasting a link like: https://www.discogs.com/release/2111-UR-The-Final-Frontier
                        </div>
                        <img
                            src="https://images.squarespace-cdn.com/content/v1/5f30083236006c73677947bf/1597164454026-1NTL28QID02MZ7Y71VZD/WORMHOLE_GREEN.png?format=1500w"
                            alt="wormhole"
                            className="floating-wormhole"
                            style={{
                                position: 'relative',
                                width: '150px',
                                height: 'auto',
                                opacity: 0.2
                            }}
                        />
                    </div>
                )}

                {error === 'discogs-url-in-voyage' && (
                    <div style={{
                        textAlign: 'center',
                        position: 'absolute',
                        top: window.innerWidth <= 600 ? '22%' : '28%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '100%',
                        maxWidth: '600px',
                        padding: window.innerWidth <= 600 ? '0 20px' : '0'
                    }}>
                        <div style={{ color: '#009e4a', fontFamily: 'CustomFont', fontSize: window.innerWidth <= 600 ? '0.65rem' : '0.75rem', lineHeight: '1.6', marginBottom: '20px', opacity: 0.8 }}>
                            That looks like a Discogs URL —{' '}
                            <span
                                onClick={() => navigate('/service?from=voyage')}
                                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                            >switch to direct mode</span>
                        </div>
                    </div>
                )}

                <div style={{ position: 'absolute', top: 'calc(40% - 55px)', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'CustomFont', fontSize: '0.7rem', letterSpacing: '0.05em', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#009e4a', cursor: 'default' }}>voyage</span>
                        <span style={{ color: 'rgba(0,158,74,0.3)' }}>/</span>
                        <span
                            onClick={() => navigate('/service?from=voyage')}
                            style={{ color: 'rgba(0,158,74,0.4)', cursor: 'pointer', transition: 'color 0.2s' }}
                            onMouseOver={e => e.currentTarget.style.color = '#009e4a'}
                            onMouseOut={e => e.currentTarget.style.color = 'rgba(0,158,74,0.4)'}
                        >direct</span>
                    </div>
                </div>

                <div className="chat-container">
                    {chatMode && clarifyMessage && (
                        <div className="chat-bubble">
                            {clarifyDisplay}
                        </div>
                    )}
                    <form onSubmit={handleSubmit} noValidate className="url-form">
                        <span className="label">{searchLabel}</span>
                        <textarea
                            ref={textareaRef}
                            id="url-input"
                            className={placeholderFading ? 'placeholder-fading' : ''}
                            value={loading ? loadingDots : url}
                            placeholder={clarifyMessage ? 'Type your answer…' : placeholderText}
                            disabled={loading}
                            readOnly={loading}
                            onChange={(e) => {
                                setUrl(e.target.value);
                                setError('');
                                setUrlError('');
                            }}
                            onInput={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSubmit(e);
                                }
                            }}
                            rows={1}
                            maxLength={500}
                            required
                        />
                        {(url || loading) && (
                            <button type="submit" className="submit-button" disabled={loading}>
                                {loading ? loadingDots : '⬆︎'}
                            </button>
                        )}
                    </form>
                </div>

                {urlError && (
                    <div style={{
                        textAlign: 'center',
                        position: 'absolute',
                        top: '28%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '100%',
                        maxWidth: '600px',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            color: '#009e4a',
                            fontFamily: 'CustomFont',
                            fontSize: '0.75rem',
                            lineHeight: '1.6',
                            marginBottom: '20px',
                            opacity: 0.8
                        }}>
                            Please enter a valid Discogs URL :)
                        </div>
                        <img
                            src="https://images.squarespace-cdn.com/content/v1/5f30083236006c73677947bf/1597164454026-1NTL28QID02MZ7Y71VZD/WORMHOLE_GREEN.png?format=1500w"
                            alt="wormhole"
                            className="floating-wormhole"
                            style={{
                                position: 'relative',
                                width: '150px',
                                height: 'auto',
                                opacity: 0.2
                            }}
                        />
                    </div>
                )}

                <div className={`recommendations ${visible ? 'visible' : 'hidden'}`}>
                    {lastSearchText && !notFound && !clarifyMessage && (
                        <div style={{
                            color: '#009e4a',
                            fontFamily: 'CustomFont',
                            fontSize: window.innerWidth <= 600 ? '0.75rem' : '0.85rem',
                            textAlign: 'center',
                            marginTop: '-40px',
                            marginBottom: '8px',
                        }}>
                            You searched: {lastSearchText}
                        </div>
                    )}
                    {chatMode && searchSummary && !notFound && (
                        <div style={{
                            color: '#009e4a',
                            fontFamily: 'CustomFont',
                            fontSize: window.innerWidth <= 600 ? '0.6rem' : '0.7rem',
                            textAlign: 'center',
                            marginBottom: '8px',
                            animation: isDive ? 'none' : 'fadeInUp 2s ease both',
                        }}>
                            {`Dig Assistant looked for: "${searchSummary}"`}
                        </div>
                    )}
                    {loading && lastSearchText && (
                        <div style={{
                            color: '#009e4a',
                            fontFamily: 'CustomFont',
                            fontSize: window.innerWidth <= 600 ? '0.65rem' : '0.75rem',
                            textAlign: 'center',
                            marginBottom: '8px',
                        }}>
                            {loadingDots}diving
                        </div>
                    )}
                    {notFound ? (
                        <span className="label">
                            {(() => {
                                // Button for 429 rate limit errors
                                if (notFoundMessage && notFoundMessage.includes("becoming a member")) {
                                    return (
                                        <div style={{ textAlign: 'center', position: 'relative' }}>
                                            <span style={{ display: 'block', marginBottom: '20px' }}>
                                                {notFoundMessage}
                                            </span>
                                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                                                <button
                                                    onClick={() => navigate('/upgrade')}
                                                    style={{
                                                        background: 'none',
                                                        border: '1px solid #009e4a',
                                                        color: '#009e4a',
                                                        padding: '6px 16px',
                                                        borderRadius: '5px',
                                                        cursor: 'pointer',
                                                        fontFamily: 'CustomFont',
                                                        fontSize: '14px',
                                                        transition: 'all 0.2s',
                                                        display: 'block'
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.target.style.background = '#009e4a';
                                                        e.target.style.color = 'black';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.target.style.background = 'none';
                                                        e.target.style.color = '#009e4a';
                                                    }}
                                                >
                                                    Get Unlimited Access
                                                </button>
                                            </div>
                                            <img
                                                src="https://images.squarespace-cdn.com/content/v1/5f30083236006c73677947bf/1597164454026-1NTL28QID02MZ7Y71VZD/WORMHOLE_GREEN.png?format=1500w"
                                                alt="wormhole"
                                                className="floating-wormhole"
                                                style={{
                                                    position: 'relative',
                                                    width: '150px',
                                                    height: 'auto',
                                                    opacity: 0.2,
                                                    display: 'block',
                                                    margin: '0 auto'
                                                }}
                                            />
                                        </div>
                                    );
                                }

                                // For other errors, display message as-is
                                return (
                                    <div style={{ textAlign: 'center', position: 'relative' }}>
                                        <span dangerouslySetInnerHTML={{ __html: notFoundMessage }} style={{ display: 'block', marginBottom: '20px' }} />
                                        <img
                                            src="https://images.squarespace-cdn.com/content/v1/5f30083236006c73677947bf/1597164454026-1NTL28QID02MZ7Y71VZD/WORMHOLE_GREEN.png?format=1500w"
                                            alt="wormhole"
                                            className="floating-wormhole"
                                            style={{
                                                position: 'relative',
                                                width: '150px',
                                                height: 'auto',
                                                opacity: 0.2
                                            }}
                                        />
                                    </div>
                                );
                            })()}
                        </span>
                    ) : releases.length > 0 ? (
                        <>
                            <div style={{ color: 'rgba(255,255,255,0.2)', textAlign: 'center', marginBottom: '12px', letterSpacing: '0.3em', fontSize: '0.7rem' }}>— — —</div>
                            {diveStack.length > 0 && (
                                <button
                                    onClick={goBack}
                                    className="submit-button"
                                    type="button"
                                    style={{ display: 'block', margin: '0 auto 12px' }}
                                >
                                    ← back
                                </button>
                            )}
                            <div style={{ animation: isDive ? 'fadeIn 0.5s ease both' : 'fadeIn 0.8s ease 1.5s both', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            {releases
                                .map((releaseId, i) => ({ releaseId, artist: recArtists[i], title: recTitles[i] }))
                                .slice(startIndex, startIndex + 5)
                                .map(({ releaseId: release, artist, title }) => {
                                    const videos = videosByReleaseId[release] || [];
                                    const hasVideos = videos.length > 0;
                                    const recordHref = `https://www.discogs.com/release/${release}`;

                                    const handleVideoClick = (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (hasVideos) {
                                            setSelectedVideoPopup({
                                                releaseId: release,
                                                artist,
                                                title,
                                                videos
                                            });
                                        }
                                    };

                                    return (
                                        <div className="recommendation-container" key={release}>
                                            <span className="recommendation">
                                                <a
                                                    href={recordHref}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => { e.stopPropagation(); }}
                                                    style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
                                                >
                                                    {`${artist} - ${title}`}
                                                </a>
                                                {hasVideos && (
                                                    <button
                                                        className="video-indicator-button"
                                                        onClick={handleVideoClick}
                                                    >
                                                        <img
                                                            src="https://storage.googleapis.com/tmhimagebucket/youtubelogogreen.svg"
                                                            alt=">>"
                                                        />
                                                    </button>
                                                )}
                                            </span>
                                            <button onClick={() => fetchReleasesDive(release)} className="dive-button">
                                                dive
                                            </button>
                                        </div>
                                    );
                                })}

                            {releases.length > 0 && releases.length <= 3 && shouldShowLowResultsMessage && (
                                <div style={{ color: '#009e4a', fontFamily: 'CustomFont', fontSize: '0.6rem', textAlign: 'center', marginTop: '10px', opacity: 0.7 }}>
                                    {consecutiveLowResultsCount >= 3 ? 'Going in circles? Try a more specific query' : 'This path looks a bit thin, try diving or clicking back and trying another path'}
                                </div>
                            )}

                            {/* Icon toolbar */}
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginTop: '18px', fontSize: '1.1rem' }}>
                                {startIndex > 0 && (
                                    <button title="Previous" onClick={() => setStartIndex(prev => Math.max(0, prev - 5))} style={{ background: 'none', border: 'none', color: '#009e4a', cursor: 'pointer', fontSize: '1.1rem', padding: '4px', opacity: 0.7 }}>←</button>
                                )}
                                {startIndex + 5 < releases.length && (
                                    <button title="More recs" onClick={() => setStartIndex(prev => prev + 5)} style={{ background: 'none', border: 'none', color: '#009e4a', cursor: 'pointer', fontSize: '1.1rem', padding: '4px', opacity: 0.7 }}>→</button>
                                )}
                                <button title="Dive history" onClick={() => setShowDiveHistory(true)} style={{ background: 'none', border: 'none', color: '#009e4a', cursor: 'pointer', fontSize: '1.1rem', padding: '4px', opacity: 0.7 }}>↺</button>
                                {deepCut && (
                                    <button title="Deep cut" onClick={() => setShowDeepCutPanel(p => !p)} className="dive-button" style={{ fontSize: '1.1rem', padding: '4px', opacity: showDeepCutPanel ? 1 : 0.7 }} onMouseOver={e => e.target.style.color = 'white'} onMouseOut={e => e.target.style.color = '#009e4a'}>deep cut</button>
                                )}
                            </div>

                            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                <button
                                    type="button"
                                    onClick={openYoutubePlaylist}
                                    style={{
                                        background: 'none',
                                        border: '1px solid rgba(252, 209, 23, 0.5)',
                                        color: 'rgba(252, 209, 23, 0.5)',
                                        padding: '6px 16px',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        fontFamily: 'CustomFont',
                                        fontSize: '14px',
                                        transition: 'all 0.2s',
                                    }}
                                    onMouseOver={(e) => { e.target.style.background = 'rgba(252, 209, 23, 0.1)'; e.target.style.color = '#fcd117'; }}
                                    onMouseOut={(e) => { e.target.style.background = 'none'; e.target.style.color = 'rgba(252, 209, 23, 0.5)'; }}
                                >
                                    Generate vinyl playlist
                                </button>
                            </div>

                            {/* Deep cut expandable */}
                            {showDeepCutPanel && deepCut && (() => {
                                const deepCutVideos = videosByReleaseId[deepCut.releaseId] || [];
                                const hasDeepCutVideos = deepCutVideos.length > 0;
                                const deepCutHref = `https://www.discogs.com/release/${deepCut.releaseId}`;
                                return (
                                    <div className="deepcut-wrap" style={{ marginTop: '10px' }}>
                                        <div className="deepcut-title">Deep cut</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span className="deepcut-link">
                                                <a href={deepCutHref} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="deepcut-text" style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}>
                                                    {deepCut.artist ? `${deepCut.artist} — ${deepCut.title}` : deepCut.title}
                                                </a>
                                                {hasDeepCutVideos && (
                                                    <button className="video-indicator-button" onClick={e => { e.preventDefault(); e.stopPropagation(); setSelectedVideoPopup({ releaseId: deepCut.releaseId, artist: deepCut.artist, title: deepCut.title, videos: deepCutVideos }); }}>
                                                        <img src="/youtubelogogreen.svg" alt=">>" />
                                                    </button>
                                                )}
                                            </span>
                                            <button onClick={() => fetchReleasesDive(deepCut.releaseId)} className="dive-button">dive</button>
                                        </div>
                                        {deepCut.label && <div className="deepcut-label">({deepCut.label})</div>}
                                    </div>
                                );
                            })()}
                            </div>
                        </>
                    ) : null}
                </div>
            </div>

            {/* Query hint — shown once, 4s after first results */}
            {showQueryHint && (
                <div
                    onClick={() => { localStorage.setItem('queryHintDismissed', '1'); setShowQueryHint(false); }}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{ background: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '20px', maxWidth: '380px', width: '100%', display: 'flex', flexDirection: 'column', gap: '14px' }}
                    >
                        <p style={{ color: 'rgba(0,158,74,0.5)', margin: 0, fontSize: '0.95rem', lineHeight: 1.6 }}>
                            Not finding what you're looking for? Try being more specific
                        </p>
                        <div style={{ color: 'rgba(255,255,255,0.2)', textAlign: 'center', letterSpacing: '0.3em', fontSize: '0.7rem' }}>— — —</div>
                        <ul style={{ color: '#009e4a', margin: 0, paddingLeft: '16px', fontSize: '0.9rem', fontStyle: 'italic', lineHeight: 2 }}>
                            <li>"Late 90s UKG like on Nice N Ripe"</li>
                            <li>"Jacuzzi Boys - Island Avenue"</li>
                        </ul>
                        <button
                            onClick={() => { localStorage.setItem('queryHintDismissed', '1'); setShowQueryHint(false); }}
                            className="submit-button"
                            style={{ alignSelf: 'center' }}
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}

            {showChatWelcome && <ChatWelcomeOverlay onClose={() => setShowChatWelcome(false)} />}

            {/* Dive History Overlay */}
            {showDiveHistory && <DiveSessionHistoryOverlay isVisible={showDiveHistory} onClose={() => setShowDiveHistory(false)} />}

            {/* Video Popup */}
            {selectedVideoPopup && (
                <VideoPopup
                    videos={selectedVideoPopup.videos}
                    releaseId={selectedVideoPopup.releaseId}
                    artist={selectedVideoPopup.artist}
                    title={selectedVideoPopup.title}
                    discogsUrl={`https://www.discogs.com/release/${selectedVideoPopup.releaseId}`}
                    onClose={() => setSelectedVideoPopup(null)}
                />
            )}
        </div>
    );
};

export default AdvancedServicePage;
